import { cableChain } from './components/dynamicModels/cableChainDynamicModel';
import {
  getFrameSubmodelKeys,
  tableFrame,
} from './components/dynamicModels/frameDynamicModel';
import { tabletop } from './components/dynamicModels/tableTopDynamicModel';
import { Header } from './components/Header/Header';
import { Sidebar } from './components/Sidebar/Sidebar';
import '@fontsource/montserrat/300.css';
import '@fontsource/montserrat/400.css';
import '@fontsource/montserrat/700.css';
import '@fontsource/lato/300.css';
import '@fontsource/lato/400.css';
import '@fontsource/lato/700.css';
import '@fontsource/sarabun/300.css';
import '@fontsource/sarabun/400.css';
import '@fontsource/sarabun/700.css';
import '@fontsource-variable/raleway/wght.css';
import { GlobalStyles, ThemeOptions } from '@mui/material';
import { useCallback } from 'react';
import { deepmerge } from '@mui/utils';
import { TypographyOptions } from '@mui/material/styles/createTypography';
import {
  addCallbacks,
  DynamicModel,
  K3Extensions,
  K3Main,
  useActions,
  useModels,
} from '@k3/core';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter';
import { USDZExporter } from 'three/examples/jsm/exporters/USDZExporter';
import { Mesh, Object3D, Vector3 } from 'three';
import { createMeshesFromMultiMaterialMesh } from 'three/examples/jsm/utils/SceneUtils';
import { forwardRef, Ref, useMemo } from 'react';
import { getCableSubmodelKeys } from './components/CableChain';
import { getTableTopSubmodelKeys } from './components/Tabletop';
import { OrderDialog } from './components/OrderDialog';
import { InvalidRuleModal } from './components/InvalidRuleModal';
import { DiscountProvider } from './store/discountContext';
import { cornerTabletop } from './components/dynamicModels/cornerTableTopDynamicModel';
import { Model } from '@k3/core/dist/src/lib/types/model.interface';
import {
  getTransformationSubmodelKeys,
  transformation,
} from './components/dynamicModels/transformationDynamicModel';
import { SceneButtons } from './components/SceneButtons';
import { PermanentMount } from './components/PermanentMount';
import { ConfirmationDialog } from './components/ConfirmationDialog';
import { CodeInputDialog } from './components/CodeInputDialog';
import { InvalidSelectionWarning } from './components/InvalidSelectionWarning';
import { GeneralProvider, useGeneralContext } from './store/generalContext';
import { Rule } from '@k3/core/dist/src/lib/types/rule.interface';
import { FullApp } from '@k3/core/dist/src/lib/types/fullApp.interface';
import { RuleItem } from '@k3/core/dist/src/lib/types/ruleitem.interface';
import { ambientLight } from './components/dynamicModels/ambientLightDynamicModel';

const dynamicModelSubmodelKeys = {
  [tableFrame.type]: getFrameSubmodelKeys,
  [cableChain.type]: getCableSubmodelKeys,
  [tabletop.type]: getTableTopSubmodelKeys,
  [cornerTabletop.type]: getTableTopSubmodelKeys,
  [transformation.type]: getTransformationSubmodelKeys,
};
const None = () => () => null;

const hiddenNavigationButtonStyles = {
  '[data-cy="dynNext"]': {
    visibility: 'hidden',
  },
  '[data-cy="dynLabel"]': {
    visibility: 'hidden',
  },
};

// avoid recomputing the styles on every render otherwise it crashes sometimes
// https://mui.com/material-ui/customization/how-to-customize/#4-global-css-override
const hiddenNavigationButtonGlobalStyles = (
  <GlobalStyles styles={hiddenNavigationButtonStyles} />
);

const extensions: K3Extensions = {
  app: {
    configurator: {
      root: (Wrapped: any) => (props: any) => {
        return (
          <>
            <Wrapped {...props} />
            <CodeInputDialog />
          </>
        );
      },
    },
    k3d: {
      sceneComponents: {
        K3Model: {
          root: (Wrapped: any) =>
            // eslint-disable-next-line react/display-name
            forwardRef((props: any, ref: Ref<any>) => {
              const models = useModels();
              const dynamicModelByIds = useMemo(() => {
                return models?.reduce((acc, m) => {
                  if (
                    m.dynamicModelKey &&
                    dynamicModelSubmodelKeys[m.dynamicModelKey]
                  ) {
                    acc[m.id] = m;
                  }
                  return acc;
                }, {} as Record<Model['id'], Model>);
              }, [models]);

              const actions = useActions();
              const subModelKeys = useMemo(() => {
                return actions?.models.reduce((acc, action) => {
                  if (dynamicModelByIds[action.modelId]) {
                    const dynamicModell = dynamicModelByIds[action.modelId];
                    if (dynamicModell.dynamicModelKey) {
                      acc.push(
                        ...dynamicModelSubmodelKeys[
                          dynamicModell.dynamicModelKey
                        ](dynamicModell)
                      );
                    }
                  }
                  return acc;
                }, [] as string[]);
              }, [actions]);

              if (subModelKeys?.includes(props.model?.key)) {
                return null;
              }

              return <Wrapped ref={ref} {...props}></Wrapped>;
            }),
        },
      },
    },
    header: {
      root: () => Header,
    },
    exitButtons: {
      root: None,
    },
    price: {
      root: None,
    },
    sidebar: {
      root: Sidebar,
      invalidSelectionWarning: {
        root: InvalidSelectionWarning,
      },
    },
    // This is so complicated because we changed the behaviour in the rc and stable version.
    // To work for both it needs to be like this.
    navigationButtons: {
      root: (Wrapped) => (props) => {
        const disabled =
          !!props.showActionButton || props.handleDisabled('forward');

        return (
          <>
            {disabled && hiddenNavigationButtonGlobalStyles}
            <Wrapped {...props} />
          </>
        );
      },
    },
    orderDialog: {
      root: OrderDialog,
      confirmation: {
        success: {
          root: ConfirmationDialog,
        },
      },
    },
    invalidRuleModal: {
      root: InvalidRuleModal,
    },
    sceneButtons: {
      root: SceneButtons,
    },
    mountedWhenLoaded: {
      root: PermanentMount,
    },
  },
};

const splitUpMeshesWithMultipleMaterials = (scene: Object3D) => {
  const meshesWithMultipleMaterials: Mesh[] = [];

  scene.traverse((child) => {
    if (child instanceof Mesh && child?.material?.length > 1) {
      meshesWithMultipleMaterials.push(child);
    }
  });

  meshesWithMultipleMaterials.forEach((mesh) => {
    const group = createMeshesFromMultiMaterialMesh(mesh);
    const position = mesh.getWorldPosition(new Vector3());

    mesh.removeFromParent();

    group.position.copy(position);
    group.updateMatrixWorld(true);

    scene.add(group);
  });

  return scene;
};

function removeNotMatchingValuesAndRuleItems(
  fullApp: FullApp,
  modellVariableId: number,
  soleModellValueId: number
) {
  const variableValues = fullApp.values.filter(
    (value) => value.variableId === modellVariableId
  );
  const unwantedValues = variableValues.filter(
    (value) =>
      value.variableId === modellVariableId && value.id !== soleModellValueId
  );

  const unwantedValueIds = unwantedValues.map((value) => value.id);

  const rulesById = fullApp.rules.reduce((acc, rule) => {
    acc[rule.id] = rule;
    return acc;
  }, {} as Record<Rule['id'], Rule>);

  const ruleItems = fullApp.ruleItems.reduce((acc, ruleItem) => {
    const rule = rulesById[ruleItem.ruleId];
    const hasModelColumn = rule.columns.some(
      (column) => column.targetId === modellVariableId
    );

    if (!hasModelColumn) {
      acc.push(ruleItem);
      return acc;
    }

    const ruleItemValues = ruleItem.values[modellVariableId] as number[];

    if (!ruleItemValues || ruleItemValues?.length === 0) {
      acc.push(ruleItem);
      return acc;
    }

    const filteredValues = ruleItemValues.filter(
      (value) => !unwantedValueIds.includes(value)
    );
    if (filteredValues.length > 0) {
      acc.push({
        ...ruleItem,
        values: { ...ruleItem.values, [modellVariableId]: filteredValues },
      });
    }
    return acc;
  }, [] as RuleItem[]);

  const values = fullApp.values.filter(
    (value) => !unwantedValueIds.includes(value.id)
  );

  const ruleIds = ruleItems.reduce((acc, ruleItem) => {
    acc[ruleItem.ruleId] = true;
    return acc;
  }, {} as Record<Rule['id'], boolean>);

  const rules = fullApp.rules.filter((rule) => ruleIds[rule.id]);

  return { values, ruleItems, rules };
}

const removeAllModelsFromRulesExceptQueryParamModel = (fullApp: FullApp) => {
  const urlParams = new URLSearchParams(window.location.search);
  const modelValueKey = urlParams.get('model') ?? urlParams.get('id');

  if (!modelValueKey) return fullApp;

  const modelValue = fullApp.values.find(
    (value) => value.key === modelValueKey
  );

  if (!modelValue) return fullApp;

  const modelVariable = fullApp.variables.find(
    (variable) => variable.id === modelValue.variableId
  );

  if (!modelVariable) return fullApp;

  const { values, ruleItems, rules } = removeNotMatchingValuesAndRuleItems(
    fullApp,
    modelVariable.id,
    modelValue.id
  );

  return {
    ...fullApp,
    values,
    ruleItems,
    rules,
  };
};

let models: DynamicModel<any, any>[];

addCallbacks({
  getDynamicModels: (coreModels) => {
    if (!models) {
      models = [
        ...coreModels,
        tabletop,
        tableFrame,
        cableChain,
        cornerTabletop,
        transformation,
        ambientLight,
      ];
    }
    return models;
  },
  setARExport: async ({ platform, scene }) => {
    const usdzExporter = new USDZExporter();
    const gltfExporter = new GLTFExporter();

    const clonedScene = splitUpMeshesWithMultipleMaterials(scene.clone());

    const arraybuffer =
      platform === 'iOS'
        ? await usdzExporter.parse(clonedScene)
        : ((await gltfExporter.parseAsync(clonedScene, {
            binary: true,
          })) as ArrayBuffer);

    const blob = new Blob([arraybuffer], {
      type: 'application/octet-stream',
    });

    return blob;
  },
  getScreenshotDimensions: () => ({ width: 1024, height: 1024 }),
  preprocessFullApp: removeAllModelsFromRulesExceptQueryParamModel,
});

const InnerApp = () => {
  const { dealerStyles } = useGeneralContext();
  const themeOptionsCreator = useCallback(
    (originalTheme: ThemeOptions) => {
      return deepmerge(originalTheme, {
        typography: {
          fontFamily:
            (dealerStyles?.font ? `${dealerStyles.font},` : '') +
            (originalTheme?.typography as TypographyOptions)?.fontFamily,
          h6: {
            fontSize: '1rem',
          },
        },
      });
    },
    [dealerStyles]
  );

  const stylesComponent = useMemo(() => {
    return (
      <GlobalStyles
        styles={{
          '[data-cy="brand"]': {
            display: 'none',
          },
          '[data-cy="simpleVariableLabel"]': {
            fontWeight: '600 !important',
            color: `${dealerStyles?.variableFontColor} !important`,
          },
        }}
      />
    );
  }, [dealerStyles]);

  return (
    <>
      {stylesComponent}
      <K3Main
        extensions={extensions}
        /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
        // @ts-ignore
        themeOptionsCreator={themeOptionsCreator}
      />
    </>
  );
};

export const App = () => {
  return (
    <DiscountProvider>
      <GeneralProvider>
        <InnerApp />
      </GeneralProvider>
    </DiscountProvider>
  );
};
